/*
 * Отказ от блюда — весь путь одной машиной.
 *
 * Раньше эта логика была размазана по четырём флагам в main и двум
 * обработчикам: правки в одном месте ломали другое. Теперь путь
 * один и читается сверху вниз:
 *
 *   нужен PIN?  ──да──▶ пад старшего ──▶ окно причины ──▶ готово
 *        └───────нет───────────────────▶ окно причины ──▶ готово
 *
 * Пад старшего работает и без интернета: если старший уже входил на
 * этой кассе, его PIN узнаваем локально (offline/session), и права
 * проверяются по-настоящему. Совсем незнакомый PIN без связи — отказ
 * пропускается с честной пометкой в отчёт: гость уже отказался, зал
 * не может ждать роутер.
 */
import { useState } from 'react';
import type { OrderState } from '@dastarhan/core';
import type { SyncEvent } from '@dastarhan/contracts';
import { api, ApiError } from '../api';
import { approverOffline, pinPrint } from '../offline/session';
import { ElevateDialog } from '../screens/ElevateDialog';
import { VoidReasonDialog } from '../screens/VoidReasonDialog';
import { applyVoid } from './logic';

type Stage =
  | { k: 'elevate' }
  | { k: 'reason'; approver: string | null; offlineNote: string | null };

export function VoidFlow({
  token, deviceKey, terminalId, order, needsPin, preselect, onCancel, onDone,
}: {
  token: string;
  deviceKey: string;
  terminalId: string;
  order: OrderState;
  /** право стоит «по PIN старшего» — сначала пад */
  needsPin: boolean;
  preselect: string | null;
  onCancel: () => void;
  onDone: (r: { after: OrderState; event: SyncEvent; toast: string }) => void;
}) {
  const [stage, setStage] = useState<Stage>(
    needsPin ? { k: 'elevate' } : { k: 'reason', approver: null, offlineNote: null },
  );

  if (stage.k === 'elevate') {
    return (
      <ElevateDialog title="Отказ от блюда"
        onCancel={onCancel}
        onConfirm={async (pin) => {
          try {
            const r = await api.elevate(token, { pin, action: 'order.item.remove' });
            setStage({ k: 'reason', approver: r.approver, offlineNote: null });
            return r.approver;
          } catch (e) {
            // Сервер жив и сказал «не подошёл» — это про PIN, не про связь
            if (e instanceof ApiError) return null;
            // Связи нет: пробуем узнать старшего локально — по пропуску
            const hit = approverOffline(await pinPrint(pin, deviceKey), 'order.item.remove');
            if (hit === 'no-right') return null;
            if (hit) {
              setStage({
                k: 'reason', approver: hit.name,
                offlineNote: 'PIN старшего проверен на кассе (офлайн)',
              });
              return hit.name;
            }
            // Незнакомый PIN без связи: зал живёт, отчёт узнает правду
            setStage({ k: 'reason', approver: null, offlineNote: 'без PIN (офлайн)' });
            return 'офлайн';
          }
        }}
      />
    );
  }

  return (
    <VoidReasonDialog
      lines={order.items.filter((i) => !i.removed).map((i) => ({
        itemId: i.itemId, name: i.name, qty: i.qty,
        unitPrice: i.unitPrice, fired: i.fired,
      }))}
      preselect={preselect}
      onCancel={onCancel}
      onConfirm={(itemId, reason, comment, qty) => {
        const r = applyVoid({
          order, itemId, qty, reason, comment, terminalId,
          approver: stage.approver, offlineNote: stage.offlineNote,
        });
        if (!r) { onCancel(); return; }
        onDone(r);
      }}
    />
  );
}
